// frontend/src/pages/admin/AdminAISystemSettings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../api/settings';
import adminApiService from '../../services/admin';
import { 
    FaRobot, FaSpinner, FaPlus, FaTrash, FaCheckCircle, 
    FaPowerOff, FaImage, FaSave, FaShieldAlt, FaPenNib, FaCogs, FaToggleOn, FaToggleOff, FaStar,
    FaSearch, FaBolt, FaCloudDownloadAlt, FaVial, FaExclamationTriangle, FaWrench,
    FaExchangeAlt
} from 'react-icons/fa';

const MASTER_ADMIN_EMAILS = ["esekiegabriel@gmail.com", "thomasesekie@gmail.com"];

function AdminAISystemSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // AI State
  const [models, setModels] = useState([]);
  const [aiConfig, setAiConfig] = useState({
      model_name: '', // Global Default
      vision_enabled: true,
      cms_models: { about_us: '', terms_of_service: '', privacy_policy: '' },
      operational_models: { 
          inspection_notes: '', 
          description_generator: '', 
          compatibility_checker: '',
          ai_mechanic: '', 
          ai_analyst: '' 
      }
  });
  const [newModel, setNewModel] = useState({ name: '', api_model_name: '', description: '' });

  // --- OpenRouter catalog ---
  const [catalog, setCatalog] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [toolsOnly, setToolsOnly] = useState(true);
  const [freeOnly, setFreeOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importing, setImporting] = useState(false);

  // --- Which AI company is answering ---
  const [provider, setProvider] = useState('openrouter');
  const [providerOptions, setProviderOptions] = useState([]);
  const [switchingProvider, setSwitchingProvider] = useState(false);

  // --- Connection status and per-model tests ---
  const [health, setHealth] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResults, setTestResults] = useState({});

  // --- SECURITY GUARD ---
  useEffect(() => {
      if (user && !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
          alert("Access Denied: This area is restricted to the Master Admin.");
          navigate('/admin');
      }
  }, [user, navigate]);

  useEffect(() => {
    if (user && MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) {
        fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
        const [settingsData, modelsData] = await Promise.all([
            settingsApi.getAllSettings(),
            adminApiService.listAllModelConfigsAdmin()
        ]);
        
        const loadedAiConfig = settingsData.ai_config || {};
        // Ensure nested objects exist
        if (!loadedAiConfig.cms_models) loadedAiConfig.cms_models = { about_us: '', terms_of_service: '', privacy_policy: '' };
        if (!loadedAiConfig.operational_models) loadedAiConfig.operational_models = { 
            inspection_notes: '', 
            description_generator: '', 
            compatibility_checker: '',
            ai_mechanic: '',
            ai_analyst: ''
        };
        
        setAiConfig(loadedAiConfig);
        setModels(modelsData || []);
        loadProvider();
        checkHealth();
    } catch (e) { 
        console.error("Error fetching AI settings:", e); 
    } finally {
        setLoading(false);
    }
  };

  // --- HANDLERS ---

  const handleAddModel = async (e) => {
      e.preventDefault();
      if(!newModel.name || !newModel.api_model_name) return alert("Name and API Model ID are required");
      try {
          await adminApiService.createModelConfigAdmin(newModel);
          setNewModel({ name: '', api_model_name: '', description: '' });
          fetchData(); 
      } catch(err) { alert("Failed to add model."); }
  };

  const handleDeleteModel = async (id) => {
      if (!window.confirm("Delete this model configuration?")) return;
      try {
          await adminApiService.deleteModelConfigAdmin(id);
          setModels(prev => prev.filter(m => m.id !== id));
      } catch(err) { alert("Failed to delete model."); }
  };
  
  // --- OPENROUTER HANDLERS ---

  const checkHealth = async () => {
      try {
          const result = await adminApiService.getAiHealth();
          setHealth(result);
          if (result && result.provider) setProvider(result.provider);
      } catch (e) {
          setHealth({ configured: false, healthy: false, message: 'Could not reach the server.' });
      }
  };

  // --- AI PROVIDER SWITCH ---

  const loadProvider = async () => {
      try {
          const result = await adminApiService.getAiProvider();
          setProvider(result.provider || 'openrouter');
          setProviderOptions(result.available || []);
      } catch (e) {
          console.error('Could not read the AI provider setting:', e);
      }
  };

  const handleSwitchProvider = async (nextProvider) => {
      if (nextProvider === provider || switchingProvider) return;

      const chosen = providerOptions.find(o => o.id === nextProvider);
      const label = chosen ? chosen.label : nextProvider;

      if (chosen && !chosen.key_present) {
          return alert(
              `Cannot switch to ${label}: ${chosen.key_name} is missing from backend/.env.\n\n` +
              `Add the key, restart the server, then try again.`
          );
      }

      if (!window.confirm(
          `Switch the whole app over to ${label}?\n\n` +
          `The AI Mechanic, the AI Analyst and every other AI feature will ` +
          `start using ${label} straight away.`
      )) return;

      setSwitchingProvider(true);
      try {
          const result = await adminApiService.setAiProvider(nextProvider);
          setProvider(result.provider);
          alert(
              result.healthy
                  ? `${result.message}\n\n${result.detail}`
                  : `${result.message}\n\nWARNING - it did not answer the test: ${result.detail}`
          );
          fetchData();
      } catch (e) {
          alert(`Could not switch: ${e.message || 'unknown error'}`);
      } finally {
          setSwitchingProvider(false);
      }
  };

  const loadCatalog = async () => {
      setCatalogLoading(true);
      setCatalogError('');
      try {
          const result = await adminApiService.getOpenRouterCatalog({
              search: catalogSearch, toolsOnly, freeOnly, provider
          });
          setCatalog(result.models || []);
      } catch (e) {
          setCatalogError(e.message || 'Could not load the model list from OpenRouter.');
          setCatalog([]);
      } finally {
          setCatalogLoading(false);
      }
  };

  const openCatalog = () => {
      setCatalogOpen(true);
      if (catalog.length === 0) loadCatalog();
  };

  const toggleSelected = (id) => {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImportSelected = async () => {
      if (selectedIds.length === 0) return alert("Tick the models you want to add first.");
      setImporting(true);
      try {
          const chosen = catalog.filter(m => selectedIds.includes(m.id)).map(m => ({
              api_model_name: m.id,
              name: m.name,
              description: m.description,
              supports_tools: m.supports_tools,
              supports_images: m.supports_images,
          }));
          const result = await adminApiService.importModels(chosen);
          alert(result.message || 'Models added.');
          setSelectedIds([]);
          fetchData();
      } catch (e) {
          alert('Could not add those models.');
      } finally {
          setImporting(false);
      }
  };

  const handleTestModel = async (apiModelName) => {
      setTesting(apiModelName);
      try {
          const result = await adminApiService.testModel(apiModelName);
          setTestResults(prev => ({ ...prev, [apiModelName]: result }));
      } catch (e) {
          setTestResults(prev => ({ ...prev, [apiModelName]: {
              success: false, message: e.message || 'The test failed.' } }));
      } finally {
          setTesting(null);
      }
  };

  const handleDeactivateAll = async () => {
      if(!window.confirm("WARNING: This will take the AI Mechanic OFFLINE. Proceed?")) return;
      try { 
          await adminApiService.deactivateAllModels(); 
          alert("AI is now offline."); 
          fetchData();
      } catch (err) { alert("Failed to deactivate."); }
  };

  const toggleVision = async () => {
      const newValue = !aiConfig.vision_enabled;
      setAiConfig(prev => ({ ...prev, vision_enabled: newValue }));
  };

  const handleGlobalDefaultChange = (e) => {
      setAiConfig(prev => ({ ...prev, model_name: e.target.value }));
  };

  // --- TASK ALLOCATION HANDLERS ---
  
  const toggleTaskActive = (category, key) => {
      setAiConfig(prev => {
          const currentVal = prev[category][key];
          // If currently disabled (value is 'disabled'), set to empty string (Use Default)
          // If currently active (value is anything else), set to 'disabled'
          const newVal = currentVal === 'disabled' ? '' : 'disabled';
          return {
              ...prev,
              [category]: {
                  ...prev[category],
                  [key]: newVal
              }
          };
      });
  };

  const handleTaskModelChange = (category, key, value) => {
      setAiConfig(prev => ({
          ...prev,
          [category]: {
              ...prev[category],
              [key]: value
          }
      }));
  };

  const saveAiConfig = async () => {
      setSaving(true);
      try { 
          await settingsApi.updateSettings('ai_config', aiConfig); 
          alert("AI Configuration Saved Successfully!"); 
      } catch (err) { 
          alert("Failed to save AI config."); 
      } finally { 
          setSaving(false); 
      }
  };

  if (loading) return <div className="p-10 text-center"><FaSpinner className="animate-spin text-3xl text-primary" /> Loading AI Systems...</div>;

  // Double check render protection
  if (user && !MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase())) return null;

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <div className="flex items-center gap-3 mb-8 border-b border-gray-200 pb-4">
          <div className="bg-red-600 text-white p-3 rounded-lg shadow-lg">
              <FaShieldAlt size={24} />
          </div>
          <div>
              <h1 className="text-2xl font-bold text-gray-800">AI System Settings</h1>
              <p className="text-sm text-red-600 font-bold flex items-center gap-1">
                  <FaShieldAlt /> RESTRICTED AREA: Master Admin Only
              </p>
          </div>
      </div>

      {/* --- AI PROVIDER SWITCH --- */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <FaExchangeAlt className="text-primary" /> AI Provider
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                  Which company answers for every AI feature in the app.
                  Switching takes effect immediately.
              </p>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {(providerOptions.length > 0 ? providerOptions : [
                  { id: 'openrouter', label: 'OpenRouter', description: '', key_present: true, key_name: 'OPENROUTER_API_KEY' },
                  { id: 'gemini', label: 'Google Gemini', description: '', key_present: true, key_name: 'GOOGLE_API_KEY' }
              ]).map(option => {
                  const isActive = provider === option.id;
                  return (
                      <button
                          key={option.id}
                          type="button"
                          disabled={switchingProvider}
                          onClick={() => handleSwitchProvider(option.id)}
                          className={`text-left p-4 rounded-lg border-2 transition-all disabled:opacity-60 ${
                              isActive
                                  ? 'border-green-500 bg-green-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-primary hover:bg-gray-50'
                          }`}
                      >
                          <div className="flex items-center justify-between mb-1">
                              <span className="font-bold text-gray-800">{option.label}</span>
                              {switchingProvider && !isActive
                                  ? <FaSpinner className="animate-spin text-gray-400" />
                                  : isActive
                                      ? <FaToggleOn className="text-green-600 text-2xl" />
                                      : <FaToggleOff className="text-gray-300 text-2xl" />}
                          </div>

                          {option.description && (
                              <p className="text-xs text-gray-500 mb-2">{option.description}</p>
                          )}

                          {isActive ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700">
                                  <FaCheckCircle /> In use now
                              </span>
                          ) : option.key_present ? (
                              <span className="text-xs text-gray-500">Click to switch</span>
                          ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                                  <FaExclamationTriangle /> {option.key_name} missing from .env
                              </span>
                          )}
                      </button>
                  );
              })}
          </div>

          <div className="px-6 pb-5 -mt-1">
              <p className="text-xs text-gray-500">
                  Each provider keeps its own model choices below, so switching back
                  and forth never loses your settings. Remember: the AI Mechanic runs
                  shop tools, so it needs a model that supports tools.
              </p>
          </div>
      </div>

      {/* --- CONNECTION STATUS --- */}
      {health && (
          <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
              health.healthy
                  ? 'bg-green-50 border-green-200'
                  : 'bg-amber-50 border-amber-200'}`}>
              {health.healthy
                  ? <FaCheckCircle className="text-green-600 mt-0.5" />
                  : <FaExclamationTriangle className="text-amber-600 mt-0.5" />}
              <div className="flex-1">
                  <p className={`text-sm font-bold ${health.healthy ? 'text-green-800' : 'text-amber-800'}`}>
                      {(() => {
                          const name = provider === 'gemini' ? 'Google Gemini' : 'OpenRouter';
                          return health.healthy ? `Connected to ${name}` : `${name} is not answering`;
                      })()}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">{health.message}</p>
                  {!health.configured && (
                      <p className="text-xs text-gray-600 mt-1">
                          Add <span className="font-mono">
                              {provider === 'gemini' ? 'GOOGLE_API_KEY' : 'OPENROUTER_API_KEY'}
                          </span> to
                          <span className="font-mono"> backend/.env</span> and restart the server.
                      </p>
                  )}
              </div>
              <button type="button" onClick={checkHealth}
                  className="text-xs text-gray-600 underline hover:text-gray-900">
                  Re-check
              </button>
          </div>
      )}

      <div className="space-y-8">
          
          {/* 1. GLOBAL DEFAULT & REGISTRY */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FaRobot className="text-primary" /> AI Brain Core</h2>
                  <button onClick={handleDeactivateAll} className="bg-red-100 text-red-600 px-4 py-2 rounded text-xs font-bold hover:bg-red-200 flex items-center gap-2"><FaPowerOff /> Emergency Kill Switch</button>
              </div>
              
              <div className="p-6">
                  {/* Global Default Selection */}
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-8">
                      <label className="block text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                          <FaStar /> Global Default Model
                      </label>
                      <p className="text-xs text-green-700 mb-3">
                          This model will be used for any task set to "Use Global Default".
                      </p>
                      <select 
                          value={aiConfig.model_name} 
                          onChange={handleGlobalDefaultChange}
                          className="w-full p-3 border border-green-300 rounded-lg bg-white font-bold text-gray-800 focus:ring-2 focus:ring-green-500"
                      >
                          <option value="" disabled>-- Select a Default Model --</option>
                          {models.map(m => (
                              <option key={m.id} value={m.api_model_name}>
                                  {m.name} ({m.api_model_name})
                              </option>
                          ))}
                      </select>
                  </div>

                  {/* Vision Toggle */}
                  <div className="flex items-center justify-between bg-blue-50 p-4 rounded-lg border border-blue-100 mb-8">
                      <div className="flex items-center gap-3">
                          <div className="bg-white p-2 rounded-full text-blue-600 shadow-sm"><FaImage /></div>
                          <div><span className="text-sm font-bold text-gray-800 block">AI Vision</span><span className="text-xs text-gray-500">Allow image uploads for analysis.</span></div>
                      </div>
                      <button onClick={toggleVision} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none border-2 ${aiConfig.vision_enabled ? 'bg-green-500 border-green-500' : 'bg-gray-300 border-gray-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${aiConfig.vision_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                  </div>

                  {/* Model Registry Table */}
                  <h3 className="text-sm font-bold text-gray-700 mb-3">Registered Models</h3>
                  <div className="overflow-x-auto mb-6">
                      <table className="w-full text-left border-collapse min-w-[600px]">
                          <thead className="bg-gray-100 text-xs text-gray-600 uppercase">
                              <tr><th className="p-3">Display Name</th><th className="p-3">API ID</th><th className="p-3">Description</th><th className="p-3 text-right">Actions</th></tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                              {models.map(model => (
                                  <tr key={model.id} className={aiConfig.model_name === model.api_model_name ? "bg-green-50" : "bg-white"}>
                                      <td className="p-3 font-bold text-sm flex items-center gap-2">
                                          {model.name}
                                          {aiConfig.model_name === model.api_model_name && <span className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded-full">DEFAULT</span>}
                                      </td>
                                      <td className="p-3 font-mono text-xs text-gray-500">{model.api_model_name}</td>
                                      <td className="p-3 text-xs text-gray-500">{model.description}</td>
                                      <td className="p-3 text-right">
                                          <button
                                              onClick={() => handleTestModel(model.api_model_name)}
                                              disabled={testing === model.api_model_name}
                                              title="Send this model one short message and see if it answers"
                                              className="text-xs text-indigo-500 hover:text-indigo-700 mr-3 disabled:opacity-40">
                                              {testing === model.api_model_name
                                                  ? <FaSpinner className="animate-spin" />
                                                  : <FaVial />}
                                          </button>
                                          <button onClick={() => handleDeleteModel(model.id)} className="text-xs text-red-400 hover:text-red-600"><FaTrash /></button>
                                          {testResults[model.api_model_name] && (
                                              <div className={`mt-1 text-[10px] ${testResults[model.api_model_name].success ? 'text-green-600' : 'text-red-600'}`}>
                                                  {testResults[model.api_model_name].success
                                                      ? `OK in ${testResults[model.api_model_name].seconds}s`
                                                      : testResults[model.api_model_name].message}
                                              </div>
                                          )}
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>

                  {/* --- OPENROUTER CATALOG --- */}
                  <div className="bg-indigo-50 p-4 rounded border border-indigo-200 mb-4">
                      <div className="flex justify-between items-center flex-wrap gap-3">
                          <div>
                              <h4 className="text-xs font-bold text-indigo-700 uppercase flex items-center gap-2">
                                  <FaCloudDownloadAlt /> OpenRouter Catalog
                              </h4>
                              <p className="text-xs text-gray-600 mt-1">
                                  Browse every model your OpenRouter key can reach and add the ones you want.
                              </p>
                          </div>
                          <button type="button" onClick={() => catalogOpen ? setCatalogOpen(false) : openCatalog()}
                              className="bg-indigo-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-indigo-700">
                              {catalogOpen ? 'Hide catalog' : 'Browse models'}
                          </button>
                      </div>

                      {catalogOpen && (
                          <div className="mt-4">
                              <div className="flex flex-wrap gap-3 items-center mb-3">
                                  <div className="flex-1 min-w-[200px] relative">
                                      <FaSearch className="absolute left-3 top-3 text-gray-400 text-xs" />
                                      <input
                                          placeholder="Search by name, e.g. gemini, claude, gpt"
                                          value={catalogSearch}
                                          onChange={e => setCatalogSearch(e.target.value)}
                                          onKeyDown={e => e.key === 'Enter' && loadCatalog()}
                                          className="w-full pl-8 p-2 border rounded text-sm" />
                                  </div>
                                  <label className="flex items-center gap-2 text-xs text-gray-700">
                                      <input type="checkbox" checked={toolsOnly}
                                          onChange={e => setToolsOnly(e.target.checked)} />
                                      Tool calling only
                                  </label>
                                  <label className="flex items-center gap-2 text-xs text-gray-700">
                                      <input type="checkbox" checked={freeOnly}
                                          onChange={e => setFreeOnly(e.target.checked)} />
                                      Free only
                                  </label>
                                  <button type="button" onClick={loadCatalog}
                                      className="bg-gray-700 text-white px-3 py-2 rounded text-xs font-bold">
                                      {catalogLoading ? <FaSpinner className="animate-spin" /> : 'Search'}
                                  </button>
                              </div>

                              <p className="text-[11px] text-gray-500 mb-2">
                                  The AI Mechanic runs your shop tools (search parts, add to cart, book a service),
                                  so it only works on a model marked TOOLS.
                              </p>

                              {catalogError && (
                                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-xs mb-3">
                                      {catalogError}
                                  </div>
                              )}

                              <div className="max-h-80 overflow-y-auto border rounded bg-white">
                                  {catalogLoading && (
                                      <div className="p-6 text-center text-gray-400 text-sm">
                                          <FaSpinner className="animate-spin inline mr-2" /> Loading models...
                                      </div>
                                  )}
                                  {!catalogLoading && catalog.length === 0 && !catalogError && (
                                      <div className="p-6 text-center text-gray-400 text-sm">No models matched.</div>
                                  )}
                                  {!catalogLoading && catalog.map(m => (
                                      <label key={m.id}
                                          className="flex items-start gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer">
                                          <input type="checkbox" className="mt-1"
                                              checked={selectedIds.includes(m.id)}
                                              onChange={() => toggleSelected(m.id)} />
                                          <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                  <span className="text-sm font-semibold text-gray-800">{m.name}</span>
                                                  {m.supports_tools && (
                                                      <span className="text-[10px] bg-green-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                                                          <FaWrench className="text-[8px]" /> TOOLS
                                                      </span>
                                                  )}
                                                  {m.supports_images && (
                                                      <span className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full">IMAGES</span>
                                                  )}
                                                  {m.is_free && (
                                                      <span className="text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full">FREE</span>
                                                  )}
                                              </div>
                                              <div className="text-[11px] font-mono text-gray-500 truncate">{m.id}</div>
                                              <div className="text-[11px] text-gray-500 mt-1">
                                                  {m.context_length ? `${Math.round(m.context_length / 1000)}k context` : ''}
                                                  {!m.is_free && ` · $${m.prompt_price_per_million}/M in · $${m.completion_price_per_million}/M out`}
                                              </div>
                                          </div>
                                      </label>
                                  ))}
                              </div>

                              <div className="flex justify-between items-center mt-3">
                                  <span className="text-xs text-gray-600">
                                      {selectedIds.length} selected of {catalog.length} shown
                                  </span>
                                  <button type="button" onClick={handleImportSelected} disabled={importing}
                                      className="bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700 disabled:opacity-50">
                                      {importing ? <FaSpinner className="animate-spin" /> : `Add ${selectedIds.length || ''} to my models`}
                                  </button>
                              </div>
                          </div>
                      )}
                  </div>

                  {/* Add New Model */}
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                      <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Register New Model</h4>
                      <form onSubmit={handleAddModel} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                          <input placeholder="Display Name" value={newModel.name} onChange={e => setNewModel({...newModel, name: e.target.value})} className="w-full p-2 border rounded text-sm" />
                          <input placeholder="API ID (e.g. google/gemini-3.1-flash-lite)" value={newModel.api_model_name} onChange={e => setNewModel({...newModel, api_model_name: e.target.value})} className="w-full p-2 border rounded text-sm font-mono" />
                          <input placeholder="Description" value={newModel.description} onChange={e => setNewModel({...newModel, description: e.target.value})} className="w-full p-2 border rounded text-sm" />
                          <button type="submit" className="w-full bg-green-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-green-700"><FaPlus /> Add</button>
                      </form>
                  </div>
              </div>
          </div>
          
          {/* 2. CMS MODEL ALLOCATION */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FaPenNib className="text-purple-600" /> CMS Content Models
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {['about_us', 'terms_of_service', 'privacy_policy'].map(key => {
                      const isEnabled = aiConfig.cms_models?.[key] !== 'disabled';
                      return (
                          <div key={key} className={`border rounded p-4 transition-colors ${isEnabled ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200 opacity-75'}`}>
                              <div className="flex justify-between items-center mb-3">
                                  <label className="block text-xs font-bold text-gray-700 uppercase">{key.replace('_', ' ')}</label>
                                  <button 
                                      onClick={() => toggleTaskActive('cms_models', key)}
                                      className={`text-xl ${isEnabled ? 'text-green-500' : 'text-gray-400'}`}
                                      title={isEnabled ? "Disable AI for this task" : "Enable AI"}
                                  >
                                      {isEnabled ? <FaToggleOn /> : <FaToggleOff />}
                                  </button>
                              </div>
                              <select 
                                  value={aiConfig.cms_models?.[key] === 'disabled' ? '' : aiConfig.cms_models?.[key]}
                                  onChange={(e) => handleTaskModelChange('cms_models', key, e.target.value)}
                                  className="w-full p-2 border rounded text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
                                  disabled={!isEnabled}
                              >
                                  <option value="">Use Global Default</option>
                                  {models.map(m => <option key={m.id} value={m.api_model_name}>{m.name}</option>)}
                              </select>
                              {!isEnabled && <p className="text-[10px] text-red-500 mt-1 font-bold">AI Disabled</p>}
                          </div>
                      );
                  })}
              </div>
          </div>

          {/* 3. OPERATIONAL MODEL ALLOCATION */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FaCogs className="text-orange-600" /> Operational Model Allocation
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {['ai_mechanic', 'ai_analyst', 'inspection_notes', 'description_generator', 'compatibility_checker'].map(key => {
                      const isEnabled = aiConfig.operational_models?.[key] !== 'disabled';
                      return (
                          <div key={key} className={`border rounded p-4 transition-colors ${isEnabled ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200 opacity-75'}`}>
                              <div className="flex justify-between items-center mb-3">
                                  <label className="block text-xs font-bold text-gray-700 uppercase">{key.replace('_', ' ')}</label>
                                  <button 
                                      onClick={() => toggleTaskActive('operational_models', key)}
                                      className={`text-xl ${isEnabled ? 'text-green-500' : 'text-gray-400'}`}
                                      title={isEnabled ? "Disable AI for this task" : "Enable AI"}
                                  >
                                      {isEnabled ? <FaToggleOn /> : <FaToggleOff />}
                                  </button>
                              </div>
                              <select 
                                  value={aiConfig.operational_models?.[key] === 'disabled' ? '' : aiConfig.operational_models?.[key]}
                                  onChange={(e) => handleTaskModelChange('operational_models', key, e.target.value)}
                                  className="w-full p-2 border rounded text-sm bg-white disabled:bg-gray-100 disabled:text-gray-400"
                                  disabled={!isEnabled}
                              >
                                  <option value="">Use Global Default</option>
                                  {models.map(m => <option key={m.id} value={m.api_model_name}>{m.name}</option>)}
                              </select>
                              {!isEnabled && <p className="text-[10px] text-red-500 mt-1 font-bold">AI Disabled</p>}
                          </div>
                      );
                  })}
              </div>
              <div className="mt-6 text-right">
                  <button onClick={saveAiConfig} disabled={saving} className="bg-green-600 text-white px-8 py-3 rounded font-bold hover:bg-green-700 shadow-lg flex items-center gap-2 ml-auto">
                      {saving ? <FaSpinner className="animate-spin" /> : <FaSave />} Save All Configurations
                  </button>
              </div>
          </div>
      </div>
    </div>
  );
}

export default AdminAISystemSettings;